import {
    ScheduleLinkedApplication,
    ScheduleLinkedSourceConflictErrorBody,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { getLinkedConflictDocumentType } from "@common/helpers/domains/accounting/scheduleDocumentLinks";
import { pluralizeWithCount } from "@common/helpers/string";
import { formatDateOnly } from "@helpers/date";

/**
 * `apply`: the user is adding a schedule to one side while the other side of
 * the match already has one. `match`: both sides are scheduled and the user is
 * matching them, so there is nothing to unmatch yet.
 */
export type ScheduleLinkedConflictContext = "apply" | "match";

export interface ScheduleLinkedConflictOption {
    kind: "keepExisting" | "keepRequested" | "unmatch";
    id: string;
    title: string;
    description: string;
    /** The schedules this option keeps; empty for the requested one and unmatch. */
    applicationIds: string[];
}

export interface ScheduleLinkedConflictView {
    title: string;
    body: string;
    options: ScheduleLinkedConflictOption[];
}

type LinkedSourceType =
    | ScheduleSourceType.invoice
    | ScheduleSourceType.bill
    | ScheduleSourceType.transaction;

interface ConflictSide {
    sourceType: LinkedSourceType;
    kind: "keepExisting" | "keepRequested";
    scheduleNames: string[];
    termStarts: string[];
    sourceCount: number;
    applicationIds: string[];
}

const SIDE_ORDER: LinkedSourceType[] = [
    ScheduleSourceType.invoice,
    ScheduleSourceType.bill,
    ScheduleSourceType.transaction,
];

const SINGLE_SOURCE_LABELS: Record<LinkedSourceType, string> = {
    [ScheduleSourceType.invoice]: "on the invoice",
    [ScheduleSourceType.bill]: "on the bill",
    [ScheduleSourceType.transaction]: "on this transaction",
};

const MULTIPLE_SOURCE_NOUNS: Record<LinkedSourceType, string> = {
    [ScheduleSourceType.invoice]: "invoice",
    [ScheduleSourceType.bill]: "bill",
    [ScheduleSourceType.transaction]: "matched transaction",
};

const SOURCE_ID_OF: Record<
    LinkedSourceType,
    (application: ScheduleLinkedApplication) => string | number | null
> = {
    [ScheduleSourceType.invoice]: (application) => application.invoiceId,
    [ScheduleSourceType.bill]: (application) => application.billId,
    [ScheduleSourceType.transaction]: (application) =>
        application.transactionId,
};

function describeSource(side: ConflictSide): string {
    if (side.sourceCount === 1) {
        return SINGLE_SOURCE_LABELS[side.sourceType];
    }
    return `on the ${pluralizeWithCount(
        MULTIPLE_SOURCE_NOUNS[side.sourceType],
        side.sourceCount,
    )}`;
}

function describeSchedule(side: ConflictSide): string {
    const names = new Set(side.scheduleNames);
    if (side.applicationIds.length > 1 || names.size > 1) {
        return "the schedules";
    }
    return `the ${side.scheduleNames[0]} schedule`;
}

function earliest(termStarts: string[]): string {
    return [...termStarts].sort()[0];
}

function toExistingSide(
    sourceType: LinkedSourceType,
    applications: ScheduleLinkedApplication[],
): ConflictSide | null {
    const matching = applications.filter(
        (application) => application.sourceType === sourceType,
    );
    if (matching.length === 0) {
        return null;
    }
    const sourceIds = new Set(matching.map(SOURCE_ID_OF[sourceType]));
    return {
        sourceType,
        kind: "keepExisting",
        scheduleNames: matching.map((application) => application.scheduleName),
        termStarts: matching.map((application) => application.termStart),
        sourceCount: sourceIds.size,
        applicationIds: matching.map(
            (application) => application.applicationId,
        ),
    };
}

function toSides(
    conflict: ScheduleLinkedSourceConflictErrorBody,
): ConflictSide[] {
    return SIDE_ORDER.flatMap((sourceType): ConflictSide[] => {
        const requested = conflict.requested;
        if (requested?.sourceType === sourceType) {
            return [
                {
                    sourceType,
                    kind: "keepRequested",
                    scheduleNames: [requested.scheduleName],
                    termStarts: [requested.termStart],
                    sourceCount: 1,
                    applicationIds: [],
                },
            ];
        }
        const existing = toExistingSide(sourceType, conflict.existing);
        return existing ? [existing] : [];
    });
}

export function buildScheduleLinkedConflictView(options: {
    conflict: ScheduleLinkedSourceConflictErrorBody;
    context: ScheduleLinkedConflictContext;
}): ScheduleLinkedConflictView {
    const sides = toSides(options.conflict);
    const startDates = new Set(sides.map((side) => earliest(side.termStarts)));
    const isSameStart = startDates.size <= 1;
    const keepSentence = isSameStart
        ? "Keep one schedule and remove the other"
        : "Keep one start date and remove the other";
    const isApply = options.context === "apply";
    const documentNoun = getLinkedConflictDocumentType(options.conflict);

    const keepOptions = sides.map((side): ScheduleLinkedConflictOption => ({
        kind: side.kind,
        id: `keep-${side.sourceType}`,
        title: `Keep ${describeSchedule(side)} ${describeSource(side)}`,
        description: `Recognition starts ${formatDateOnly(
            earliest(side.termStarts),
            "MMM d, yyyy",
        )}`,
        applicationIds: side.applicationIds,
    }));

    return {
        title: isSameStart
            ? `This ${documentNoun} and transaction both have a schedule`
            : `This ${documentNoun} and transaction start recognition on different dates`,
        body: isApply
            ? `${keepSentence}, or unmatch this ${documentNoun} and transaction.`
            : `${keepSentence}.`,
        options: isApply
            ? [
                  ...keepOptions,
                  {
                      kind: "unmatch",
                      id: "unmatch",
                      title: `Unmatch ${documentNoun} and transaction`,
                      description: "Match will be removed",
                      applicationIds: [],
                  },
              ]
            : keepOptions,
    };
}
